import { supabase } from "./supabase";
import type {
  AdminOverview,
  AdminUserList,
  AppConfigRow,
  AuditLog,
  Campaign,
  ErrorEvent,
  Experiment,
  FeatureFlag,
  FunnelStep,
  LibraryDeck,
  LibraryReport,
  PushVariantStat,
  RetentionRow,
  UserDetail,
} from "./types";

function unwrap<T>(data: T | null, error: { message: string } | null): T {
  if (error) throw new Error(error.message);
  if (data === null) throw new Error("No data returned");
  return data;
}

export async function fetchOverview(): Promise<AdminOverview> {
  const { data, error } = await supabase.rpc("admin_overview");
  return unwrap(data as AdminOverview | null, error);
}

export async function fetchFunnel(days = 14): Promise<FunnelStep[]> {
  const { data, error } = await supabase.rpc("admin_funnel", { p_days: days });
  return unwrap(data as FunnelStep[] | null, error);
}

export async function fetchRetention(weeks = 8): Promise<RetentionRow[]> {
  const { data, error } = await supabase.rpc("admin_retention", { p_weeks: weeks });
  return unwrap(data as RetentionRow[] | null, error);
}

export async function fetchUsers(search: string, page: number, pageSize = 25): Promise<AdminUserList> {
  const { data, error } = await supabase.rpc("admin_user_list", {
    p_search: search.trim() || null,
    p_limit: pageSize,
    p_offset: page * pageSize,
  });
  return unwrap(data as AdminUserList | null, error);
}

export async function fetchUserDetail(id: string): Promise<UserDetail> {
  const { data, error } = await supabase.rpc("admin_user_detail", { p_user: id });
  return unwrap(data as UserDetail | null, error);
}

export async function fetchLibrary(status: LibraryDeck["status"] | "all"): Promise<LibraryDeck[]> {
  let query = supabase
    .from("library_decks")
    .select("*, profiles!library_decks_owner_user_id_fkey(display_name), library_screen_results(note, model, attempts)")
    .order("created_at", { ascending: false })
    .limit(100);
  if (status !== "all") query = query.eq("status", status);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as LibraryDeck[];
}

export async function fetchReports(): Promise<LibraryReport[]> {
  const { data, error } = await supabase
    .from("library_reports")
    .select("*, library_decks(title), profiles!library_reports_reporter_user_id_fkey(display_name)")
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as LibraryReport[];
}

export async function setLibraryStatus(
  id: string,
  status: LibraryDeck["status"],
  note?: string,
): Promise<void> {
  const patch: Record<string, unknown> = { status, moderation_note: note ?? null };
  if (status === "approved") patch.published_at = new Date().toISOString();
  const { error } = await supabase.from("library_decks").update(patch).eq("id", id);
  if (error) throw error;
  await recordAudit(`library_${status}`, "library_deck", id, { note: note ?? null });
}

export async function resolveReport(
  id: string,
  status: LibraryReport["status"],
): Promise<void> {
  const { error } = await supabase
    .from("library_reports")
    .update({ status, resolved_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
  await recordAudit(`report_${status}`, "library_report", id, {});
}

export async function fetchCampaigns(): Promise<Campaign[]> {
  const { data, error } = await supabase
    .from("push_campaigns")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  return (data ?? []) as Campaign[];
}

export async function saveCampaign(payload: {
  id?: string;
  name: string;
  segment: Record<string, unknown>;
  title_a: string;
  body_a: string;
  title_b?: string | null;
  body_b?: string | null;
  deep_link?: string | null;
  scheduled_for?: string | null;
  status: Campaign["status"];
}): Promise<Campaign> {
  const { data, error } = await supabase
    .from("push_campaigns")
    .upsert(payload)
    .select("*")
    .single();
  if (error) throw error;
  await recordAudit(payload.id ? "campaign_updated" : "campaign_created", "push_campaign", data.id, {
    status: payload.status,
  });
  return data as Campaign;
}

const PUSH_ERRORS: Record<string, string> = {
  fcm_not_configured:
    "Firebase isn't configured yet. Set the FCM_SERVICE_ACCOUNT secret and rebuild the app with google-services.json.",
  no_audience:
    "No devices have registered for push yet. Install a build with google-services.json, then open the app.",
  campaign_copy_missing: "Title and body are required before sending.",
  already_sent: "This campaign was already sent.",
  campaign_cancelled: "This campaign was cancelled.",
  campaign_not_found: "That campaign no longer exists.",
  audience_failed: "Couldn't resolve the audience. Check the segment and try again.",
  campaign_update_failed: "Couldn't lock the campaign for sending. Try again.",
};

async function pushError(error: unknown): Promise<Error> {
  const context = (error as { context?: Response }).context;
  if (context && typeof context.clone === "function") {
    try {
      const body = (await context.clone().json()) as { error?: string };
      if (body?.error) {
        return new Error(PUSH_ERRORS[body.error] ?? `Push failed: ${body.error}`);
      }
    } catch {
      // The function returned a non-JSON body; fall through to the generic error.
    }
  }
  return error instanceof Error ? error : new Error(String(error));
}

export async function sendCampaign(id: string): Promise<{ sent: number; failed: number }> {
  // push-send writes the authoritative audit entry server-side.
  const { data, error } = await supabase.functions.invoke("push-send", {
    body: { campaign_id: id },
  });
  if (error) throw await pushError(error);
  return data as { sent: number; failed: number };
}

export async function fetchVariantStats(campaignId?: string): Promise<PushVariantStat[]> {
  const { data, error } = await supabase.rpc("admin_push_variant_stats", {
    p_campaign: campaignId ?? null,
  });
  if (error) throw error;
  return (data ?? []) as PushVariantStat[];
}

export async function audienceCount(segment: Record<string, unknown>): Promise<number> {
  const { data, error } = await supabase.rpc("admin_push_audience", { p_segment: segment });
  if (error) throw error;
  return Number(data ?? 0);
}

export async function fetchFlags(): Promise<FeatureFlag[]> {
  const { data, error } = await supabase.from("feature_flags").select("*").order("key");
  if (error) throw error;
  return (data ?? []) as FeatureFlag[];
}

export async function setFlag(
  key: string,
  patch: Partial<Pick<FeatureFlag, "enabled" | "rollout_percent" | "payload">>,
): Promise<void> {
  const { error } = await supabase.from("feature_flags").update(patch).eq("key", key);
  if (error) throw error;
  await recordAudit("flag_updated", "feature_flag", key, patch);
}

export async function fetchExperiments(): Promise<Experiment[]> {
  const { data, error } = await supabase.from("experiments").select("*").order("key");
  if (error) throw error;
  return (data ?? []) as Experiment[];
}

export async function setExperiment(
  key: string,
  patch: Partial<Pick<Experiment, "status" | "traffic_percent">>,
): Promise<void> {
  const { error } = await supabase.from("experiments").update(patch).eq("key", key);
  if (error) throw error;
  await recordAudit("experiment_updated", "experiment", key, patch);
}

export async function fetchConfig(): Promise<AppConfigRow[]> {
  const { data, error } = await supabase.from("app_config").select("*").order("key");
  if (error) throw error;
  return (data ?? []) as AppConfigRow[];
}

export async function saveConfig(key: string, value: unknown): Promise<void> {
  const { error } = await supabase.from("app_config").update({ value }).eq("key", key);
  if (error) throw error;
  await recordAudit("config_updated", "app_config", key, {});
}

export async function fetchErrors(severity?: ErrorEvent["severity"]): Promise<ErrorEvent[]> {
  let query = supabase
    .from("error_events")
    .select("*")
    .order("occurred_at", { ascending: false })
    .limit(100);
  if (severity) query = query.eq("severity", severity);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as ErrorEvent[];
}

export async function fetchAuditLogs(): Promise<AuditLog[]> {
  const { data, error } = await supabase
    .from("audit_log")
    .select("*, profiles(display_name)")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw error;
  return (data ?? []) as AuditLog[];
}

export async function recordAudit(
  action: string,
  targetType: string | null,
  targetId: string | null,
  metadata: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase.rpc("admin_record_audit", {
    p_action: action,
    p_target_type: targetType,
    p_target_id: targetId,
    p_metadata: metadata,
  });
  if (error) console.warn("audit failed", error.message);
}

export async function createExperiment(input: {
  key: string;
  name: string;
  variants: string[];
  traffic_percent: number;
}): Promise<void> {
  const { error } = await supabase.from("experiments").insert({
    ...input,
    status: "draft",
  });
  if (error) throw error;
  await recordAudit("experiment_created", "experiment", input.key, {});
}

export async function fetchExperimentAssignments(
  experimentKey: string,
): Promise<Array<{ experiment_key: string; user_id: string; variant: string; assigned_at: string }>> {
  const { data, error } = await supabase
    .from("experiment_assignments")
    .select("experiment_key, user_id, variant, assigned_at")
    .eq("experiment_key", experimentKey)
    .order("assigned_at", { ascending: false })
    .limit(100);
  if (error) throw error;
  return data ?? [];
}

export async function recordExperimentAssignment(
  experimentKey: string,
  userId: string,
  variant: string,
): Promise<void> {
  const { error } = await supabase.from("experiment_assignments").insert({
    experiment_key: experimentKey,
    user_id: userId,
    variant,
  });
  if (error) throw error;
  await recordAudit("experiment_assigned", "experiment_assignment", `${experimentKey}:${userId}`, {
    experiment_key: experimentKey,
    user_id: userId,
    variant,
  });
}
